<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// Card "Perfil do cliente" + popover de origem do "Histórico de cliente".
// Extraído de HistoricoCliente.vue sem alteração de markup, classes ou copy.
import { computed, getCurrentInstance } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisInfoRow from 'dashboard/components-next/LaisInfoRow.vue';
import BuyerProfileTag from './BuyerProfileTag.vue';
import { STATUS_LABEL } from '../utils/leadHistoryConstants';

const props = defineProps({
  lead: { type: Object, required: true },
  buyerProfile: { type: Object, default: null },
  businessContext: { type: String, default: '' },
  showOrigem: { type: Boolean, default: false },
  origemHasData: { type: Boolean, default: false },
  origemResumo: { type: String, default: '' },
  origemRows: { type: Array, default: () => [] },
});

defineEmits(['toggleOrigem']);

const { uid } = getCurrentInstance();
const originDrawerDescriptionId = `lead-origin-drawer-description-${uid}`;
const orcamentoText = computed(() => {
  const o = props.lead.additional_attributes?.orcamento;
  return o || props.lead.custom_attributes?.orcamento || 'Não informado';
});

const statusLabel = computed(
  () =>
    STATUS_LABEL[props.lead.qualification_status] ||
    props.lead.qualification_status
);
const originDrawerDescription = computed(() => {
  if (!props.origemHasData) {
    return 'Abre os detalhes da origem do cliente. Nenhuma origem foi rastreada.';
  }

  if (props.origemResumo) {
    return `Abre os detalhes da origem do cliente. ${props.origemResumo}`;
  }

  return `Abre os detalhes da origem do cliente com ${props.origemRows.length} campo(s) disponível(is).`;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Perfil do cliente">
    <template v-if="lead.qualification_status" #titleAction>
      <span
        class="max-w-full break-words rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide [overflow-wrap:anywhere]"
        :class="{
          'text-amber-700 bg-amber-50':
            lead.qualification_status === 'nao_qualificado',
          'text-emerald-700 bg-emerald-50':
            lead.qualification_status === 'qualificado',
          'text-lais-primary bg-[#f0e8fa]':
            lead.qualification_status === 'pronto_atendimento',
        }"
      >
        {{ statusLabel }}
      </span>
    </template>
    <div class="flex flex-col">
      <LaisInfoRow
        icon="i-lucide-target"
        label="Objetivo"
        :value="lead.additional_attributes?.objetivo"
      />
      <LaisInfoRow
        icon="i-lucide-map-pin"
        label="Localização"
        :value="lead.additional_attributes?.localizacao || lead.location"
      />
      <LaisInfoRow icon="i-lucide-wallet" label="Orçamento">
        {{ orcamentoText }}
      </LaisInfoRow>
      <LaisInfoRow
        icon="i-lucide-briefcase"
        label="Especificidades"
        :value="lead.additional_attributes?.especificidades"
      />
    </div>
    <div class="mt-4">
      <div class="mb-3 flex flex-wrap gap-2">
        <BuyerProfileTag
          :profile="buyerProfile"
          :business-context="businessContext"
          size="sm"
        />
      </div>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label="Abrir origem do cliente"
        :aria-describedby="originDrawerDescriptionId"
        :data-legacy-popover-open="showOrigem || undefined"
        class="group inline-flex w-full items-center justify-between gap-3 rounded-lais border border-lais-border bg-white px-4 py-3 text-left text-[14px] font-medium text-lais-text transition-colors hover:border-lais-primary hover:bg-lais-primary-50/40 focus-visible:outline-none focus-visible:shadow-lais-focus"
        @click="$emit('toggleOrigem')"
      >
        <span class="flex min-w-0 items-center gap-2">
          <span
            class="i-lucide-map-pin size-4 shrink-0 text-lais-icon"
            aria-hidden="true"
          />
          <span class="min-w-0 break-words [overflow-wrap:anywhere]">
            Origem do cliente
          </span>
        </span>
        <span
          class="i-lucide-chevron-right size-4 shrink-0 text-lais-icon transition-colors group-hover:text-lais-primary"
          aria-hidden="true"
        />
      </button>
      <span :id="originDrawerDescriptionId" class="sr-only">
        {{ originDrawerDescription }}
      </span>
    </div>
  </LaisCard>
</template>
